import { DatePipe } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { lastValueFrom } from 'rxjs';
import { AuthService } from 'src/app/core/services/auth.service';
import { ContractSignatureType } from 'src/app/domain/enums/contract-signature-type.enum';
import { IContractSignature } from 'src/app/domain/registration/models/IContractSignature';
import { DialogConfirmComponent } from 'src/app/shared/components/dialog-confirm/dialog-confirm.component';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { AddSignatureDialogComponent } from './add-signature-dialog/add-signature-dialog.component';
import { ContractTemplateEditorComponent } from './contract-template-editor/contract-template-editor.component';
import { ContractGenerationService } from '../../services/contract-generation.service';
import { ContractsService } from '../../crud-service';
import {
  IContractDialogData,
  IContractablePlan,
  IContractComponentConfig,
  IContractableEntityService,
  IRelatedPersons,
  ContractEntityType
} from 'src/app/domain/registration/interfaces/contract-generation.interfaces';
import { AbstractContractGenerationService } from 'src/app/shared/services/contract-generation/abstract-contract-generation.service';
import { ContractPreviewDialogComponent } from './contract-preview-dialog/contract-preview-dialog.component';

@Component({
  selector: 'app-contracts-dialog',
  templateUrl: './contracts-dialog.component.html',
  styleUrls: ['./contracts-dialog.component.sass']
})
export class ContractsDialogComponent implements OnInit {
  contractForm: FormGroup;
  signatures = new MatTableDataSource<IContractSignature>([]);
  displayedColumns: string[] = ['name', 'email', 'document', 'action', 'actions'];
  signatureTypes = ContractSignatureType;

  plans: IContractablePlan[] = [];
  selectedPlan: IContractablePlan | null = null;
  isTemplateEdited: boolean = false;
  editedTemplateContent: string | null = null;
  loading: boolean = false;
  isLoading: boolean = false;
  contractCount: number = 0;
  defaultContractedCustomer: any;
  relatedPersons: IRelatedPersons = {};
  config: IContractComponentConfig;

  private entityService: IContractableEntityService;
  private generationService: AbstractContractGenerationService;

  constructor(
    public dialogRef: MatDialogRef<ContractsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IContractDialogData & {
      entityService: IContractableEntityService;
      generationService: AbstractContractGenerationService;
      config?: IContractComponentConfig;
    },
    private fb: FormBuilder,
    private dialog: MatDialog,
    private datePipe: DatePipe,
    private authService: AuthService,
    private toastService: ToastService,
    private contractsService: ContractsService,
    private contractGenerationService: ContractGenerationService
  ) {
    if (!data?.entity) {
      this.toastService.showError('Dados da entidade não encontrados');
      this.dialogRef.close();
      return;
    }

    if (!data?.entityService) {
      this.toastService.showError('Serviço da entidade não fornecido');
      this.dialogRef.close();
      return;
    }

    if (!data?.generationService) {
      this.toastService.showError('Serviço de geração não fornecido');
      this.dialogRef.close();
      return;
    }

    this.entityService = data.entityService;
    this.generationService = data.generationService;
    this.config = {
      title: 'Gerar Contrato',
      allowTemplateEdit: true,
      autoAddRelatedSignatures: true,
      autoAddDefaultContracted: true,
      autoAddCurrentUser: true,
      ...data.config
    };

    this.contractForm = this.fb.group({
      content: ['', Validators.required],
      planId: ['', Validators.required]
    });

    if (this.config.autoAddCurrentUser) {
      this.addCurrentUserSignature();
    }
  }

  ngOnInit() {
    this.loadPlans();

    if (this.config.autoAddDefaultContracted) {
      this.loadDefaultContractedCustomer();
    }

    if (this.config.autoAddRelatedSignatures) {
      this.loadRelatedPersons();
    }

    this.addEntitySignature();
  }

  private loadPlans() {
    if (!this.data?.entity?.id) {
      this.toastService.showError('ID da entidade não encontrado');
      this.dialogRef.close();
      return;
    }

    this.entityService.getPlans(this.data.entity.id).subscribe({
      next: (plans: IContractablePlan[]) => {
        this.plans = plans;

        if (this.plans.length === 0) {
          this.toastService.showError(`${this.getEntityTypeName()} não possui planos ativos`);
          this.dialogRef.close();
          return;
        }

        this.loadExistingContracts();
      },
      error: (error) => {
        console.error('❌ Erro ao carregar planos:', error);
        this.toastService.showError(`Erro ao carregar planos do ${this.getEntityTypeName()}`);
        this.dialogRef.close();
      }
    });
  }

  /**
   * Carrega informações das pessoas relacionadas à entidade
   */
  private loadRelatedPersons() {
    if (!this.data?.entity?.id) return;

    this.entityService.getRelatedPersons(this.data.entity.id).subscribe({
      next: (relatedPersons: IRelatedPersons) => {
        this.relatedPersons = relatedPersons;

        console.log('📋 Pessoas relacionadas carregadas:', relatedPersons);

        // Adicionar assinaturas de pessoas relacionadas
        this.addRelatedPersonsSignatures();
      },
      error: (error) => {
        console.error('❌ Erro ao buscar informações de pessoas relacionadas:', error);
      }
    });
  }

  /**
   * Obtém o nome do tipo de entidade para mensagens
   */
  private getEntityTypeName(): string {
    const typeNames: Record<ContractEntityType, string> = {
      customer: 'Cliente',
      partner: 'Parceiro',
      consultant: 'Consultor',
      representative: 'Representante',
      counter: 'Contador',
      unit: 'Unidade'
    };

    return typeNames[this.data.entityType] || 'Entidade';
  }

  /**
   * Carrega contratos existentes e atualiza status dos planos
   */
  private loadExistingContracts() {
    const contractsObservable = this.data.entityType === 'customer'
      ? this.contractsService.getContractsByCustomer(this.data.entity.id)
      : this.contractsService.getContractsByPartner(this.data.entity.id);

    contractsObservable.subscribe({
      next: (contracts: any[]) => {
        console.log('🔍 Contratos carregados:', contracts);

        // Atualiza os planos com informações detalhadas dos contratos
        this.plans = this.plans.map(plan => {
          // Verifica se existe um contrato para este plano específico
          const planContract = contracts.find(contract => contract.planId === plan.id);

          if (planContract) {
            // Verificar se o contrato está assinado
            const isContractSigned = this.generationService.isContractSigned(planContract.status);

            console.log(`✅ Plano ${plan.id} (${plan.system?.name}): contrato ${planContract.id}, status=${planContract.status}, isContractSigned=${isContractSigned}`);

            return {
              ...plan,
              hasContract: true,
              contractId: planContract.id,
              contractStatus: planContract.status,
              canRemoveContract: !isContractSigned
            };
          }

          console.log(`ℹ️ Plano ${plan.id} (${plan.system?.name}): sem contrato`);
          return {
            ...plan,
            hasContract: false,
            contractId: undefined,
            contractStatus: undefined,
            canRemoveContract: false
          };
        });
      },
      error: (error) => {
        console.error('❌ Erro ao carregar contratos existentes:', error);
        this.toastService.showError('Erro ao carregar contratos existentes');
      }
    });
  }

  /**
   * Trata seleção de um plano
   */
  onPlanSelect(plan: IContractablePlan) {
    // Se o plano tem contrato assinado, não permite seleção
    if (plan.hasContract && this.generationService.isContractSigned(plan.contractStatus)) {
      this.toastService.showWarn('Este plano já possui um contrato assinado e não pode ser selecionado novamente.');
      return;
    }

    // Se o plano tem contrato não assinado, oferece opção de removê-lo primeiro
    if (plan.hasContract && plan.canRemoveContract) {
      this.confirmAndRemoveExistingContract(plan);
      return;
    }

    // Caso não tenha contrato, seleciona normalmente
    this.selectPlan(plan);
  }

  /**
   * Confirma e remove um contrato existente
   */
  private confirmAndRemoveExistingContract(plan: IContractablePlan) {
    const confirmDialog = this.dialog.open(DialogConfirmComponent, {
      width: '400px',
      data: {
        title: 'Remover contrato existente',
        message: 'Este plano já possui um contrato em andamento que ainda não foi assinado. Deseja remover o contrato atual para criar um novo?',
        confirmText: 'Sim, remover',
        cancelText: 'Não'
      }
    });

    confirmDialog.afterClosed().subscribe(result => {
      if (result) {
        this.loading = true;
        this.contractsService.removeContract(plan.contractId!).subscribe({
          next: () => {
            this.loading = false;
            // Atualiza o status do plano
            plan.hasContract = false;
            plan.contractId = undefined;
            plan.contractStatus = undefined;
            plan.canRemoveContract = false;

            // Seleciona o plano após remover o contrato
            this.selectPlan(plan);
            this.toastService.showSuccess('Contrato removido com sucesso.');
          },
          error: (error) => {
            this.loading = false;
            console.error('Erro ao remover contrato:', error);
            this.toastService.showError('Não foi possível remover o contrato.');
          }
        });
      }
    });
  }

  /**
   * Seleciona um plano para geração de contrato
   */
  private selectPlan(plan: IContractablePlan) {
    // Verificar se o plano tem um template de contrato associado
    if (!this.generationService.hasContractTemplate(plan)) {
      this.toastService.showError('O plano selecionado não possui um template de contrato associado.');
      return;
    }

    // Se tem template, prosseguir com a seleção
    this.selectedPlan = plan;
    this.contractForm.patchValue({
      planId: plan.id,
      content: plan.system!.contractTemplateId
    });

    // Atualiza o estado do formulário para validar corretamente
    this.contractForm.updateValueAndValidity();
  }

  /**
   * Carrega o cliente padrão (contratada) da API
   */
  private loadDefaultContractedCustomer() {
    this.contractGenerationService.getDefaultContractedCustomer().subscribe({
      next: (defaultCustomer) => {
        if (defaultCustomer && defaultCustomer.id) {
          console.log('✅ Cliente padrão (contratada) carregado:', defaultCustomer);
          this.defaultContractedCustomer = defaultCustomer;
          this.addContractedSignature(defaultCustomer);
        } else {
          console.warn('⚠️ Cliente padrão (contratada) não encontrado');
        }
      },
      error: (error) => {
        console.error('❌ Erro ao carregar cliente padrão (contratada):', error);
      }
    });
  }

  /**
   * Adiciona a assinatura do cliente padrão (contratada)
   */
  private addContractedSignature(defaultCustomer: any) {
    if (!defaultCustomer) return;

    const contractedSignature = this.generationService.createSignatureFromEntity(
      defaultCustomer,
      ContractSignatureType.CONTRATADA,
      'CONTRATADA',
      { isDefaultContracted: true }
    );

    // Sobrescrever customerId para identificar como cliente padrão
    contractedSignature.customerId = '0';

    console.log('📋 Assinatura da contratada adicionada:', contractedSignature);

    // Adicionar ao início das assinaturas para destacar
    this.signatures.data = [contractedSignature, ...this.signatures.data];
  }

  /**
   * Adiciona assinatura da entidade principal (contratante)
   */
  private addEntitySignature() {
    if (!this.data?.entity) {
      console.error('Dados da entidade não encontrados');
      return;
    }

    const entityInfo = this.generationService.extractEntityInfo(this.data.entity);
    console.log('📱 Informações da entidade extraídas:', entityInfo);

    const entitySignature = this.generationService.createSignatureFromEntity(
      this.data.entity,
      ContractSignatureType.CONTRATANTE,
      '001',
      { isContracted: true }
    );

    console.log('📋 Assinatura da entidade criada:', entitySignature);

    this.signatures.data = [entitySignature];
  }

  /**
   * Adiciona assinatura do usuário atual (interveniente)
   */
  private addCurrentUserSignature() {
    const currentUser = this.authService.getUser();
    if (!currentUser) return;

    console.log('👤 Dados do usuário atual:', currentUser);

    const userSignature = this.generationService.createSignatureFromEntity(
      currentUser,
      ContractSignatureType.INTERVENIENTE,
      '002'
    );

    console.log('📋 Assinatura do usuário atual criada:', userSignature);

    this.signatures.data = [...this.signatures.data, userSignature];
  }

  /**
   * Adiciona uma nova assinatura manualmente
   */
  addSignature() {
    const excludedTypes = this.config.excludedSignatureTypes ||
      [ContractSignatureType.EMISSOR, ContractSignatureType.CONTRATANTE];

    const dialogRef = this.dialog.open(AddSignatureDialogComponent, {
      width: '500px',
      data: { excludedTypes }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const newSignature: IContractSignature = {
          code: this.generationService.generateSignatureCode(this.signatures.data.length),
          name: result.name,
          email: result.email,
          document: result.document || '',
          type: result.type,
          role: result.role || result.type,
          whatsAppNumber: result.whatsAppNumber || '',
          status: 'PENDENTE',
          isContracted: false
        };

        console.log('📝 Nova assinatura adicionada:', newSignature);
        this.signatures.data = [...this.signatures.data, newSignature];
      }
    });
  }

  /**
   * Edita uma assinatura existente
   */
  editSignature(signature: IContractSignature, index: number) {
    const excludedTypes = this.config.excludedSignatureTypes ||
      [ContractSignatureType.EMISSOR, ContractSignatureType.CONTRATANTE];

    const dialogRef = this.dialog.open(AddSignatureDialogComponent, {
      width: '500px',
      data: {
        excludedTypes,
        signature: {
          ...signature,
          type: signature.role || signature.type
        }
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const updatedSignatures = [...this.signatures.data];
        updatedSignatures[index] = {
          ...signature,
          name: result.name,
          email: result.email,
          document: result.document || signature.document || '',
          type: result.type,
          role: result.role || result.type,
          whatsAppNumber: result.whatsAppNumber || signature.whatsAppNumber || '',
          isContracted: false
        };

        console.log('📝 Assinatura atualizada:', updatedSignatures[index]);
        this.signatures.data = updatedSignatures;
      }
    });
  }

  /**
   * Remove uma assinatura
   */
  public removeSignature(index: number) {
    const signature = this.signatures.data[index];

    // Validações
    if (signature.isDefaultContracted) {
      this.toastService.showWarn('A contratada principal não pode ser removida');
      return;
    }

    if (signature.isContracted) {
      this.toastService.showWarn('O contratante principal não pode ser removido');
      return;
    }

    if (signature.type === this.signatureTypes.EMISSOR) {
      this.toastService.showWarn('A assinatura do emissor não pode ser removida');
      return;
    }

    // Remove a assinatura
    const updatedSignatures = [...this.signatures.data];
    updatedSignatures.splice(index, 1);
    this.signatures.data = updatedSignatures;
  }

  /**
   * Obtém label de um tipo de assinatura
   */
  getSignatureTypeLabel(type: ContractSignatureType): string {
    return this.generationService.getSignatureTypeLabel(type);
  }

  formatDate(date: Date | undefined): string {
    if (!date) return 'Não informado';
    return this.datePipe.transform(date, 'dd/MM/yyyy HH:mm:ss') || 'Formato inválido';
  }

  /**
   * Adiciona assinaturas de pessoas relacionadas ao contrato
   */
  private addRelatedPersonsSignatures() {
    if (!this.relatedPersons || Object.keys(this.relatedPersons).length === 0) {
      console.log('ℹ️ Nenhuma pessoa relacionada para adicionar');
      return;
    }

    // Obter os emails já existentes nas assinaturas
    const existingEmails = new Set(this.signatures.data.map(sig => sig.email.toLowerCase()));

    // Criar assinaturas usando o generationService
    const relatedSignatures = this.generationService.createRelatedPersonsSignatures(
      this.relatedPersons,
      existingEmails,
      () => this.generationService.generateSignatureCode(this.signatures.data.length)
    );

    if (relatedSignatures.length > 0) {
      console.log(`📋 ${relatedSignatures.length} assinatura(s) de pessoas relacionadas adicionadas:`, relatedSignatures);
      this.signatures.data = [...this.signatures.data, ...relatedSignatures];
    } else {
      console.log('ℹ️ Nenhuma assinatura nova de pessoas relacionadas foi adicionada (emails já existentes)');
    }
  }

  /**
   * Abre o editor de template para visualizar/editar o template do contrato
   */
  openTemplateEditor() {
    if (!this.selectedPlan || !this.selectedPlan.system?.contractTemplateId) {
      this.toastService.showError('Selecione um plano que tenha um template de contrato associado');
      return;
    }

    this.loading = true;
    this.contractGenerationService.getTemplateHtmlContent(this.selectedPlan.system.contractTemplateId).subscribe({
      next: (content: string) => {
        this.loading = false;

        // Abrir o editor de template com o conteúdo
        const dialogRef = this.dialog.open(ContractTemplateEditorComponent, {
          width: '90vw',
          height: '90vh',
          maxWidth: '100vw',
          maxHeight: '100vh',
          panelClass: 'template-editor-dialog',
          data: {
            templateContent: content,
            templateId: this.selectedPlan!.system!.contractTemplateId,
            planId: this.selectedPlan!.id,
            systemId: this.selectedPlan!.system!.id,
            customerId: this.data.entity.id,
            isTemporary: true // Usar modo temporário (não salva alterações permanentemente)
          }
        });

        dialogRef.afterClosed().subscribe(result => {
          if (result && result.saved && result.content) {
            // Armazenar o conteúdo editado para uso posterior na geração do contrato
            this.editedTemplateContent = result.content;

            // Indicar que o template foi editado
            this.isTemplateEdited = true;

            this.toastService.showSuccess('Template editado com sucesso para este contrato');
          }
        });
      },
      error: (error) => {
        this.loading = false;
        console.error('Erro ao obter conteúdo do template:', error);
        this.toastService.showError('Não foi possível carregar o template para edição');
      }
    });
  }

  /**
   * Restaura o template original se houver algum template editado
   */
  resetTemplate() {
    if (this.isTemplateEdited) {
      this.editedTemplateContent = null;
      this.isTemplateEdited = false;
      this.toastService.showInfo('Template restaurado para a versão original');
    }
  }

  /**
   * Gera o contrato com base nos dados fornecidos
   */
  public generateContract() {
    console.log('[ContractModal] generateContract: INÍCIO');

    if (!this.contractForm.valid) {
      this.toastService.showWarn('Preencha todos os campos obrigatórios');
      return;
    }

    // Validar assinaturas
    const assinaturasInvalidas = this.signatures.data.filter(sig => !sig.document || !sig.email);
    if (assinaturasInvalidas.length > 0) {
      this.toastService.showWarn(`${assinaturasInvalidas.length} signatário(s) com informações incompletas. Todos os signatários precisam ter CPF/CNPJ e email.`);
      return;
    }

    // Verificar se tem contratada (se configurado)
    if (this.config.autoAddDefaultContracted && !this.signatures.data.some(sig => sig.type === ContractSignatureType.CONTRATADA)) {
      if (this.defaultContractedCustomer) {
        this.addContractedSignature(this.defaultContractedCustomer);
      } else {
        this.toastService.showWarn('Não foi possível incluir a contratada no contrato. Tente novamente.');
        this.loadDefaultContractedCustomer();
        return;
      }
    }

    console.log(`[ContractModal] ${this.signatures.data.length} signatários:`, this.signatures.data);

    // Preparar dados do contrato usando o generationService
    const relatedPersonIds = this.data.entityType === 'customer' && this.relatedPersons
      ? {
        partnerId: this.relatedPersons['partnerId'] || this.relatedPersons.partner?.id,
        consultantId: this.relatedPersons['consultantId'] || this.relatedPersons.consultant?.id,
        representativeId: this.relatedPersons['representativeId'] || this.relatedPersons.representative?.id,
        counterId: this.relatedPersons['counterId'] || this.relatedPersons.counter?.id,
        unitId: this.relatedPersons['unitId'] || this.relatedPersons.unit?.id
      }
      : {};

    const contractData = this.generationService.prepareContractData(
      this.data.entity.id,
      this.data.entityType,
      this.selectedPlan!.id,
      this.selectedPlan!.system!.id,
      this.signatures.data,
      relatedPersonIds,
      this.isTemplateEdited ? this.editedTemplateContent || undefined : undefined
    );

    console.log('[ContractModal] Dados do contrato preparados:', contractData);

    // Abrir preview
    if (this.isTemplateEdited && this.editedTemplateContent) {
      this.openPreviewWithEditedTemplate(contractData, this.editedTemplateContent);
    } else {
      this.openPreviewWithOriginalTemplate(contractData);
    }
  }

  /**
   * Abre a prévia do contrato com o template original
   */
  private openPreviewWithOriginalTemplate(contractData: any) {
    const dialogRef = this.dialog.open(ContractPreviewDialogComponent, {
      width: '90vw',
      height: '90vh',
      maxWidth: '95vw',
      maxHeight: '95vh',
      data: {
        contractData,
        isPartnerPreview: this.data.entityType !== 'customer'
      },
      disableClose: false
    });

    const sub = dialogRef.componentInstance?.confirmGeneration.subscribe((genData: any) => {
      this.proceedWithGeneration(genData, dialogRef);
    });

    dialogRef.afterClosed().subscribe(() => {
      sub?.unsubscribe();
      this.loading = false;
    });
  }

  /**
   * Abre a prévia do contrato com o template editado
   */
  private openPreviewWithEditedTemplate(contractData: any, templateContent: string) {
    this.loading = true;
    this.contractGenerationService.previewWithEditedHtml(contractData, templateContent).subscribe({
      next: (pdfBlob: Blob) => {
        this.loading = false;
        const customContractData = {
          ...contractData,
          htmlContent: templateContent,
          useCustomTemplate: true
        };

        const dialogRef = this.dialog.open(ContractPreviewDialogComponent, {
          width: '90vw',
          height: '90vh',
          maxWidth: '95vw',
          maxHeight: '95vh',
          data: {
            contractData: customContractData,
            customPdfBlob: pdfBlob,
            isPartnerPreview: this.data.entityType !== 'customer'
          },
          disableClose: false
        });

        const sub = dialogRef.componentInstance?.confirmGeneration.subscribe((genData: any) => {
          const finalGenData = { ...genData, htmlContent: templateContent };
          this.proceedWithGeneration(finalGenData, dialogRef);
        });

        dialogRef.afterClosed().subscribe(() => {
          sub?.unsubscribe();
          this.loading = false;
        });
      },
      error: (error) => {
        this.loading = false;
        console.error('Erro ao gerar prévia:', error);
        this.toastService.showError('Não foi possível gerar a prévia do contrato com o template editado');
      }
    });
  }

  /**
   * Executa a geração final do contrato após confirmação da prévia
   */
  private async proceedWithGeneration(generationData: any, previewDialogRef: MatDialogRef<ContractPreviewDialogComponent>): Promise<void> {
    console.log('[ContractModal] Procedendo com geração:', generationData);

    this.loading = true;
    try {
      previewDialogRef.componentInstance?.setGenerating(true);

      const result = await lastValueFrom(this.contractGenerationService.generateContract(generationData));
      console.log('[ContractModal] Contrato gerado:', result);

      const entityTypeName = this.getEntityTypeName();
      if (result && result.id) {
        this.toastService.showSuccess(`Contrato gerado e enviado para assinaturas com sucesso!`);
        previewDialogRef.close(result);
        this.dialogRef.close(result);
      } else {
        this.toastService.showSuccess(`Contrato enviado para assinatura.`);
        previewDialogRef.close(true);
        this.dialogRef.close(true);
      }
    } catch (error: any) {
      console.error('[ContractModal] Erro na geração:', error);
      const errorMsg = error?.error?.message || error?.message || 'Erro desconhecido ao gerar contrato';
      this.toastService.showError(`Erro ao gerar contrato: ${errorMsg}`);
      previewDialogRef.close();
      this.dialogRef.close();
    } finally {
      this.loading = false;
    }
  }

  close() {
    console.log('[ContractModal] close: INÍCIO/FIM'); // LOG
    this.dialogRef.close();
  }

  /**
   * Remove um contrato existente para um plano
   */
  removeExistingContract(plan: any) {
    if (!plan.contractId || !plan.canRemoveContract) {
      this.toastService.showError('Não é possível remover este contrato.');
      return;
    }

    // Confirmar com o usuário
    this.dialog.open(DialogConfirmComponent, {
      data: {
        title: 'Remover Contrato',
        message: 'Tem certeza que deseja remover este contrato? Esta ação não pode ser desfeita.'
      }
    }).afterClosed().subscribe(result => {
      if (result) {
        this.isLoading = true;

        this.contractsService.removeContract(plan.contractId!).subscribe({
          next: () => {
            // Atualizar status do plano na UI
            plan.hasContract = false;
            plan.contractId = null;
            plan.contractStatus = null;
            plan.canRemoveContract = false;

            // Atualizar o contador de contratos do cliente
            this.contractCount--;

            this.toastService.showSuccess('Contrato removido com sucesso!');
            this.isLoading = false;
          },
          error: (error) => {
            console.error('❌ Erro ao remover contrato:', error);
            this.toastService.showError(`Erro ao remover contrato: ${error.message}`);
            this.isLoading = false;
          }
        });
      }
    });
  }

  // Método auxiliar para obter a label de um tipo de assinatura
  getSignatureColor(type: ContractSignatureType): string {
    switch (type) {
      case ContractSignatureType.CONTRATADA:
        return 'primary'; // Azul
      case ContractSignatureType.CONTRATANTE:
        return 'accent';  // Verde
      case ContractSignatureType.EMISSOR:
        return 'warn';    // Vermelho
      default:
        return '';        // Cor padrão
    }
  }
}
